import React, { FC, ReactNode, useEffect, useMemo, useRef } from 'react';

import { StorageContext, StorageState } from 'context/storage/types';

import { createStateContainer } from 'utility/Provide/StateContainer';

interface StorageContextProviderProps<Type> {
    children: ReactNode;
    initialState: Type;
}

export function createStorageContextProvider<Type>(
    context: StorageContext<Type>,
    providerName = 'StorageContextProvider'
): FC<StorageContextProviderProps<Type>> {
    function StorageContextProvider({
        children,
        initialState,
    }: StorageContextProviderProps<Type>) {
        const value = useMemo<StorageState<Type>>(() => {
            const container = createStateContainer(initialState);

            return {
                getState: () => container.getState(),
                subscribe: (subscription) => container.subscribe(subscription),
                synchronize: (subscription) =>
                    container.synchronize(subscription),
                update: (update) => {
                    const currentState = container.getState();
                    const partialState =
                        typeof update === 'function'
                            ? update(currentState)
                            : update;
                    container.setState({
                        ...currentState,
                        ...partialState,
                    });
                },
            };
        }, []); // eslint-disable-line

        // used to expose state to dev-tools
        const ref = useRef(value.getState());
        useEffect(
            () =>
                value.subscribe((state) => {
                    ref.current = state;
                }),
            [value]
        );

        return <context.Provider value={value}>{children}</context.Provider>;
    }

    StorageContextProvider.displayName = `${providerName}(${context.displayName})`;

    return StorageContextProvider;
}
